import React from 'react';
import type { VanLifeState, VanLifeAction, Card } from '../../../../games/van-life/van-life';
import { SUIT_COLOR, CardFace } from './cards';

interface Props {
  state: VanLifeState;
  myId: string;
  dispatch: (action: VanLifeAction) => void;
}

export default function TrickPanel({ state, myId, dispatch }: Props) {
  const m = state.match!;
  const partner = state.players.find(p => p.id !== myId)!;
  const followId = state.players.find(p => p.id !== m.leadPlayerId)?.id;
  const isMyLead   = m.subPhase === 'lead'   && m.leadPlayerId === myId;
  const isMyFollow = m.subPhase === 'follow' && followId === myId;
  const myHand = m.hands[myId] ?? [];
  const partnerHandCount = (m.hands[partner?.id] ?? []).length;

  // Determine playable cards — mirrors getValidActions suit-following logic
  let playableCards = myHand;
  if (m.deckLeads && m.currentDeckCard) {
    const ds = m.currentDeckCard.suit;
    if (ds !== 'Wild' && myHand.some(c => c.suit === ds))
      playableCards = myHand.filter(c => c.suit === ds || c.suit === 'Wild');
  } else if (m.subPhase === 'follow' && isMyFollow) {
    const ls = m.leadCard?.suit;
    if (ls && ls !== 'Wild' && myHand.some(c => c.suit === ls))
      playableCards = myHand.filter(c => c.suit === ls || c.suit === 'Wild');
  }
  const playable = (isMyLead || isMyFollow) ? playableCards : [];
  const playableIds = new Set(playable.map(c => c.id));

  const leadPlayer  = state.players.find(p => p.id === m.leadPlayerId);
  const followPlayer = state.players.find(p => p.id === followId);
  const prevTrick   = m.completedTricks.length > 0
    ? m.completedTricks[m.completedTricks.length - 1] : null;
  const allTricks   = m.completedTricks;

  // Suit restriction hint
  const suitHint = (() => {
    if (!isMyLead && !isMyFollow) return null;
    if (m.deckLeads && m.currentDeckCard) {
      const ds = m.currentDeckCard.suit;
      if (ds !== 'Wild' && myHand.some(c => c.suit === ds))
        return `You have ${ds} cards — must follow deck suit`;
    } else if (m.subPhase === 'follow' && isMyFollow && m.leadCard) {
      const ls = m.leadCard.suit;
      if (ls !== 'Wild' && myHand.some(c => c.suit === ls))
        return `You have ${ls} cards — must follow led suit`;
    }
    return null;
  })();

  return (
    <div className="flex flex-col gap-3">

      {/* Trick header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-amber-200">
          Trick {m.trickNumber} / {m.totalTricks}
        </h2>
        <div className="text-sm text-gray-500">
          Deck tricks: {m.deckTrickCount}
        </div>
      </div>

      {/* Three-column trick display */}
      <div className="grid grid-cols-3 gap-2">

        {/* Deck column */}
        <div className={`rounded-lg border p-3 flex flex-col items-center gap-1
          ${m.deckLeads ? 'border-amber-600 bg-amber-950/25' : 'border-gray-700 bg-gray-900/30'}`}>
          <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Park Deck</div>
          {m.currentDeckCard === null
            ? <div className="text-xs text-gray-600">exhausted</div>
            : m.deckLeads
              ? <div className="text-xs text-amber-400 font-semibold">▶ leads</div>
              : <div className="text-xs text-gray-600">goes last</div>}
          {m.currentDeckCard === null
            ? (
              <div className="w-14 h-20 rounded-lg border-2 border-dashed border-gray-700 bg-gray-900/40
                flex items-center justify-center">
                <span className="text-gray-600 text-xs text-center px-1">no cards</span>
              </div>
            )
            : m.deckLeads
              ? <CardFace card={m.currentDeckCard} size="md" />
              : (
                <div className="w-14 h-20 rounded-lg border-2 border-gray-600 bg-gray-800
                  flex items-center justify-center">
                  <span className="text-gray-500 text-2xl">?</span>
                </div>
              )
          }
          {m.deckLeads && m.currentDeckCard && (
            <div className="text-xs mt-0.5 font-semibold"
              style={{ color: SUIT_COLOR[m.currentDeckCard.suit] ?? '#999' }}>
              {m.currentDeckCard.suit === 'Wild' ? 'Wild — wilds compete' : m.currentDeckCard.suit}
            </div>
          )}
        </div>

        {/* Lead player column */}
        <div className={`rounded-lg border p-3 flex flex-col items-center gap-1
          ${!m.deckLeads ? 'border-blue-600 bg-blue-950/20' : 'border-gray-700 bg-gray-900/30'}`}>
          <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
            {leadPlayer?.name ?? '?'}{leadPlayer?.id === myId ? ' (you)' : ''}
          </div>
          {!m.deckLeads
            ? <div className="text-xs text-blue-400 font-semibold">▶ leads</div>
            : <div className="text-xs text-transparent">·</div>}
          {m.leadCard
            ? <CardFace card={m.leadCard} size="md" />
            : (
              <div className={`w-14 h-20 rounded-lg border-2 border-dashed flex items-center justify-center
                ${isMyLead ? 'border-amber-500' : 'border-gray-600'}`}>
                <span className={`text-xs ${isMyLead ? 'text-amber-400' : 'text-gray-600'}`}>
                  {isMyLead ? 'play' : '…'}
                </span>
              </div>
            )
          }
          {!m.deckLeads && m.leadCard && (
            <div className="text-xs mt-0.5 font-semibold"
              style={{ color: SUIT_COLOR[m.leadCard.suit] ?? '#999' }}>
              led: {m.leadCard.suit}
            </div>
          )}
        </div>

        {/* Follow player column */}
        <div className="rounded-lg border border-gray-700 bg-gray-900/30 p-3 flex flex-col items-center gap-1">
          <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
            {followPlayer?.name ?? '?'}{followPlayer?.id === myId ? ' (you)' : ''}
          </div>
          <div className="text-xs text-transparent">·</div>
          <div className={`w-14 h-20 rounded-lg border-2 border-dashed flex items-center justify-center
            ${isMyFollow ? 'border-amber-500' : 'border-gray-600'}`}>
            <span className={`text-xs ${isMyFollow ? 'text-amber-400' : 'text-gray-600'}`}>
              {isMyFollow ? 'play' : '…'}
            </span>
          </div>
        </div>
      </div>

      {/* Turn indicator */}
      <div className="text-center text-sm">
        {isMyLead && (
          <span className="text-amber-300 font-semibold">
            {m.deckLeads ? 'Your turn — play a card' : 'Your turn — lead with any card'}
          </span>
        )}
        {isMyFollow && (
          <span className="text-amber-300 font-semibold">Your turn — play a card to follow</span>
        )}
        {!isMyLead && !isMyFollow && (
          <span className="text-gray-500">
            Waiting for {m.subPhase === 'lead' ? leadPlayer?.name : followPlayer?.name}…
          </span>
        )}
      </div>

      {/* Previous trick */}
      {prevTrick && (() => {
        const isDeckWin = prevTrick.winnerId === 'deck';
        const winnerName = isDeckWin ? 'Park Deck'
          : state.players.find(p => p.id === prevTrick.winnerId)?.name ?? '?';
        // Show deck card last in order when player led that trick (absent if deck exhausted)
        const playerEntries = Object.entries(prevTrick.cards).filter(([id]) => id !== 'deck');
        const entries: Array<[string, Card]> = prevTrick.cards['deck']
          ? [...playerEntries, ['deck', prevTrick.cards['deck']]]
          : playerEntries;
        return (
          <div className={`rounded-lg border p-3 ${isDeckWin
            ? 'border-red-800 bg-red-950/15' : 'border-gray-700 bg-gray-900/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 uppercase tracking-wide">
                Trick {prevTrick.trickNumber}
              </span>
              <span className={`text-xs font-semibold ml-auto
                ${isDeckWin ? 'text-red-400' : 'text-green-400'}`}>
                → {winnerName} wins
              </span>
            </div>
            <div className="flex gap-3 justify-center">
              {entries.map(([id, card]) => {
                const isWinner = id === prevTrick.winnerId;
                const name = id === 'deck' ? 'Deck'
                  : state.players.find(p => p.id === id)?.name ?? id;
                return (
                  <div key={id} className="flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-600">{name}</span>
                    <div className={isWinner
                      ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-gray-900 rounded-lg' : ''}>
                      <CardFace card={card} size="sm" />
                    </div>
                    <span className={`text-xs ${isWinner ? 'text-green-400' : 'text-transparent'}`}>✓</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* My hand */}
      <div className="rounded-xl border border-gray-700 bg-gray-900/50 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-gray-500 uppercase tracking-widest">Your hand</div>
          <div className="text-xs text-gray-600">{partner?.name}: {partnerHandCount} cards</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {myHand.map(card => (
            <CardFace
              key={card.id}
              card={card}
              size="md"
              disabled={!playableIds.has(card.id)}
              highlight={playableIds.has(card.id) && (isMyLead || isMyFollow)}
              onClick={playableIds.has(card.id)
                ? () => dispatch({ type: 'playCard', playerId: myId, cardId: card.id })
                : undefined}
            />
          ))}
          {myHand.length === 0 && <span className="text-gray-600 text-sm">No cards left</span>}
        </div>
        {suitHint && (
          <div className="mt-2 text-xs text-amber-600">{suitHint} (highlighted)</div>
        )}
      </div>

      {/* Trick history */}
      {allTricks.length > 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/30 p-3">
          <div className="text-xs text-gray-600 mb-2 uppercase tracking-widest">Trick history</div>
          <div className="space-y-0.5">
            {allTricks.map(t => {
              const wName = t.winnerId === 'deck' ? 'Deck'
                : state.players.find(p => p.id === t.winnerId)?.name ?? '?';
              const cardStr = (id: string, c: Card) => {
                const n = id === 'deck' ? 'Dk' : (state.players.find(p => p.id === id)?.name?.[0] ?? '?');
                return `${n}:${c.suit === 'Wild' ? 'W' : c.suit[0]}${c.rank}`;
              };
              // Player entries first, then deck (deck absent if exhausted that trick)
              const playerParts = Object.entries(t.cards).filter(([id]) => id !== 'deck').map(([id, c]) => cardStr(id, c));
              const deckPart   = t.cards['deck'] ? cardStr('deck', t.cards['deck']) : 'Dk:—';
              return (
                <div key={t.trickNumber} className="text-xs text-gray-600 flex gap-1">
                  <span className="w-12 flex-shrink-0">T{t.trickNumber}</span>
                  <span>{playerParts.join('  ')}  {deckPart}</span>
                  <span className={`ml-auto flex-shrink-0 font-semibold
                    ${t.winnerId === 'deck' ? 'text-red-600' : 'text-green-600'}`}>
                    {wName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
